import { CanvasFocusVisibility } from "./CanvasFocusVisibility.js";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type MouseEvent as ReactMouseEvent,
  type DragEvent as ReactDragEvent,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useViewport,
  Background,
  BackgroundVariant,
  MiniMap,
  type Node as RFNode,
  type Edge as RFEdge,
  type ReactFlowProps,
  type ReactFlowInstance,
} from "@xyflow/react";
import * as Y from "yjs";
import { cn } from "../lib/utils.js";
import { WidgetLayer } from "./WidgetLayer.js";
import { PresenceCursors } from "./PresenceCursors.js";
import { CanvasConnectionBanner } from "./CanvasConnectionBanner.js";
import { CanvasRevealController } from "./CanvasRevealController.js";
import type { CanvasProps } from "./types.js";
import { ExpandedCardHostProvider } from "../widgets/ExpandedCardModal.js";
import { setCanvasAwarenessField } from "./awareness.js";
import { CanvasCursorPublisher } from "./CanvasCursorPublisher.js";
import { CanvasPinchForwarder } from "./CanvasPinchForwarder.js";
import { CanvasNavigationContext } from "../lib/canvas-navigation-context.js";
import { useCanvasNavigation } from "./useCanvasNavigation.js";
import { CANVAS_PAN_CLASSES } from "./canvas-pan-classes.js";

/**
 * The reusable canvas primitive. Owns ReactFlow chrome only (dot grid +
 * pan / zoom + viewport bounds) and renders module-specific content via slots:
 *   - `background` — phase swimlanes (SOLVE), value-stream layout (VSM), ...
 *   - `toolbarSlot` — module-specific add-widget toolbar (bottom-center)
 *   - widgets — read from `doc.getMap('widgets')`, rendered outside the
 *     ReactFlow node graph as an absolutely-positioned overlay
 *
 * A module may also supply an optional `graph` (ADR-0021): real ReactFlow
 * nodes/edges rendered inside this one `<ReactFlow>` instance, alongside the
 * overlays. Omitted for SOLVE/Hoshin — the graph-absent path is today's
 * empty-graph config, byte for byte. See {@link CanvasGraphProps}.
 */
const DEFAULT_MIN_ZOOM = 0.5;
const DEFAULT_PAN_MARGIN = 200;
const DEFAULT_MAX_ZOOM = 1.5;
const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 0.5 } as const;

// Stable identities so the graph-absent path never churns ReactFlow's store.
const EMPTY_NODES: RFNode[] = [];
const EMPTY_EDGES: RFEdge[] = [];
// Canvas widens RF's `"Backspace"`-only default to also accept Delete; hoisted
// so the graph-present path passes a stable array identity each render.
const DEFAULT_DELETE_KEYS: string[] = ["Backspace", "Delete"];

export function Canvas({
  doc,
  frameClassName,
  graph,
  dimensions,
  widgetKinds: _widgetKinds,
  background,
  reservedTop,
  topSlot,
  toolbarSlot,
  viewportOverlaySlot,
  hudSlot,
  statusSlot,
  awareness,
  showCursors = true,
  showRemoteCursors = true,
  readOnly,
  allowReadOnlySelection = false,
  resolveWidgetReadOnly,
  resolveWidgetVisibility,
  resolveWidgetLockState,
  solveId,
  widgetHostMetadata,
  connectionState,
  onReconnect,
  onWidgetRemove,
  onWidgetAskAI,
  onWidgetSelectionChange,
  onWidgetDragEnd,
  onCanvasMutation,
  minZoom = DEFAULT_MIN_ZOOM,
  wheelBehavior = "zoom",
  panMargin = DEFAULT_PAN_MARGIN,
  canvasMinX = 0,
  canvasMinY = 0,
  maxZoom = DEFAULT_MAX_ZOOM,
  defaultViewport = DEFAULT_VIEWPORT,
  showMiniMap = false,
  showWidgets = true,
  revealRequest,
}: CanvasProps): ReactElement {
  const widgetsMap = useMemo(
    () => doc.getMap<Y.Map<unknown>>("widgets"),
    [doc],
  );
  const [expandedCardHost, setExpandedCardHost] =
    useState<HTMLDivElement | null>(null);
  const [canvasRoot, setCanvasRoot] = useState<HTMLDivElement | null>(null);
  const navigation = useCanvasNavigation(canvasRoot);
  const panning = navigation.tool === "pan";
  // The overlay content host (widgets, notes, background) — where a pinch is
  // forwarded to the camera (CanvasPinchForwarder).
  const [contentHostEl, setContentHostEl] = useState<HTMLDivElement | null>(
    null,
  );
  const setContentHost = useCallback(
    (node: HTMLDivElement | null) => {
      node?.toggleAttribute("inert", panning);
      setContentHostEl(node);
    },
    [panning],
  );
  const setCanvasHosts = useCallback((node: HTMLDivElement | null) => {
    setExpandedCardHost(node);
    setCanvasRoot(node);
  }, []);
  // Selection is Canvas-owned (was local to WidgetLayer) so select/deselect
  // updates the visual ring AND the awareness fields together, with no desync.
  // `null` deselects (pane click, via `deselect` below). Pointer-leave /
  // window-blur intentionally do NOT deselect — they clear only the cursor (see
  // CanvasCursorPublisher for the multi-window rationale).
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  /* A revealed widget is OUTLINED, never selected. Selection is a semantic
     act — it publishes awareness and tells peers this person is working on
     that widget — and navigating to see something is not working on it. */
  const [highlightedWidgetId, setHighlightedWidgetId] = useState<string | null>(
    null,
  );
  const selectionChangeRef = useRef(onWidgetSelectionChange);
  useEffect(() => {
    selectionChangeRef.current = onWidgetSelectionChange;
  }, [onWidgetSelectionChange]);
  const selectWidget = useCallback(
    (widgetId: string | null): void => {
      setSelectedWidgetId(widgetId);
      setCanvasAwarenessField(awareness, "selection", widgetId);
      setCanvasAwarenessField(
        awareness,
        "activeWidget",
        widgetId ? { id: widgetId, mode: "selected" } : null,
      );
      selectionChangeRef.current?.(
        widgetId,
        widgetId ? (widgetsMap.get(widgetId) ?? null) : null,
      );
    },
    [awareness, widgetsMap],
  );
  // Stable deselect for the pane click — clears the local ring AND the
  // awareness fields together (no desync).
  const deselect = useCallback((): void => selectWidget(null), [selectWidget]);

  // Cursor privacy is independent from the rest of Canvas awareness. A host
  // can stop publishing/rendering pointers without disconnecting semantic
  // selection/activity or its trusted participant-presence model.
  const cursorAwareness = showCursors ? awareness : null;
  useEffect(() => {
    if (!showCursors) setCanvasAwarenessField(awareness, "cursor", null);
  }, [awareness, showCursors]);

  // Canvas owns semantic selection/activity lifecycle. Keeping this cleanup
  // separate from the cursor publisher lets cursor visibility change at
  // runtime without clearing a participant's deliberate widget focus.
  useEffect(
    () => () => {
      setCanvasAwarenessField(awareness, "selection", null);
      setCanvasAwarenessField(awareness, "activeWidget", null);
    },
    [awareness],
  );

  // Drop a selection when its layer or widget becomes hidden. Local React
  // state uses the guarded "adjust state during render" pattern; the epoch
  // then drives the effect that synchronizes the external Yjs awareness state.
  const selectedWidget = selectedWidgetId
    ? widgetsMap.get(selectedWidgetId)
    : undefined;
  const selectedWidgetHidden =
    selectedWidgetId !== null &&
    (!showWidgets ||
      (resolveWidgetVisibility !== undefined &&
        (selectedWidget === undefined ||
          !resolveWidgetVisibility(selectedWidget))));
  const [hiddenSelectionEpoch, setHiddenSelectionEpoch] = useState(0);
  if (selectedWidgetHidden) {
    setSelectedWidgetId(null);
    setHiddenSelectionEpoch((epoch) => epoch + 1);
  }
  useEffect(() => {
    if (hiddenSelectionEpoch === 0) return;
    setCanvasAwarenessField(awareness, "selection", null);
    setCanvasAwarenessField(awareness, "activeWidget", null);
    selectionChangeRef.current?.(null, null);
  }, [awareness, hiddenSelectionEpoch]);

  // ADR-0021 graph passthrough. The RF graph is interactive only when a module
  // supplied `graph` AND we are not read-only (Viewer). Otherwise every flag
  // matches today's hardcoded-false config, so the graph-absent path is a
  // byte-for-byte no-op for SOLVE/Hoshin. Handle inertness rides on
  // nodesConnectable → the node component reads `isConnectable` to hide handles.
  const graphInteractive = graph != null && readOnly !== true;

  // Shared RF instance for screen→flow conversion on pane clicks and drops.
  const rfInstanceRef = useRef<ReactFlowInstance | null>(null);

  // Pane click composes Canvas-owned widget deselect FIRST, then the module's
  // graph pane-click (one RF callback, two consumers, no desync).
  const graphOnPaneClick = graph?.onPaneClick;
  const graphOnPaneClickAt = graph?.onPaneClickAt;
  const handlePaneClick = useCallback(
    (event: ReactMouseEvent): void => {
      if (panning) return;
      deselect();
      graphOnPaneClick?.(event);
      const instance = rfInstanceRef.current;
      if (graphInteractive && instance && graphOnPaneClickAt) {
        graphOnPaneClickAt(
          event,
          instance.screenToFlowPosition({ x: event.clientX, y: event.clientY }),
        );
      }
    },
    [deselect, graphInteractive, graphOnPaneClick, graphOnPaneClickAt, panning],
  );

  // Capture the RF instance so drop conversion can run screen→flow inside the
  // provider without a consumer ref-bridge (ADR-0021). onInit fires ~1 frame
  // after mount (before any humanly possible drop) and again on a real remount
  // with a fresh instance; StrictMode fires it exactly once. Stable identity.
  const handleInit = useCallback((instance: ReactFlowInstance): void => {
    rfInstanceRef.current = instance;
  }, []);

  // Drop wrapper: preventDefault (so the browser doesn't navigate on a stray
  // file/link drop), convert screen→flow, hand the module flow-space coords.
  // The `!instance` guard is defensive — the onInit window closes before a drop
  // is reachable, but a pre-init call degrades to a no-op, never a throw.
  const graphOnDrop = graph?.onDrop;
  const handleDrop = useCallback(
    (event: ReactDragEvent): void => {
      event.preventDefault();
      const instance = rfInstanceRef.current;
      if (!instance) return;
      const flowPosition = instance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      graphOnDrop?.(event, flowPosition);
    },
    [graphOnDrop],
  );

  const graphOnNodeClick = graph?.onNodeClick;
  const graphOnNodeClickAt = graph?.onNodeClickAt;
  const handleNodeClickAt = useCallback(
    (event: ReactMouseEvent, node: RFNode): void => {
      graphOnNodeClick?.(event, node);
      const instance = rfInstanceRef.current;
      if (!instance || !graphOnNodeClickAt) return;
      graphOnNodeClickAt(
        event,
        node,
        instance.screenToFlowPosition({ x: event.clientX, y: event.clientY }),
      );
    },
    [graphOnNodeClick, graphOnNodeClickAt],
  );

  // Build the RF prop overrides for the graph. Under exactOptionalPropertyTypes
  // RF's optionals reject an explicit `undefined`, so assign ONLY defined keys
  // and spread — absence (not undefined) is what keeps the graph-absent path a
  // true no-op. Value-typed fields use `!== undefined` (connectionRadius: 0 must
  // forward; deleteKeyCode: null must NOT coalesce to the default).
  const graphRfProps: Partial<ReactFlowProps> = {};
  if (graph) {
    if (graph.nodeTypes) graphRfProps.nodeTypes = graph.nodeTypes;
    if (graph.edgeTypes) graphRfProps.edgeTypes = graph.edgeTypes;
    if (graph.onNodeClickAt) graphRfProps.onNodeClick = handleNodeClickAt;
    else if (graph.onNodeClick) graphRfProps.onNodeClick = graph.onNodeClick;
    if (graph.onNodeDoubleClick)
      graphRfProps.onNodeDoubleClick = graph.onNodeDoubleClick;
    if (graph.onEdgeClick) graphRfProps.onEdgeClick = graph.onEdgeClick;
    if (graph.onEdgeDoubleClick)
      graphRfProps.onEdgeDoubleClick = graph.onEdgeDoubleClick;
    if (graph.onSelectionChange)
      graphRfProps.onSelectionChange = graph.onSelectionChange;
    if (graph.defaultEdgeOptions)
      graphRfProps.defaultEdgeOptions = graph.defaultEdgeOptions;
    if (graph.connectionRadius !== undefined)
      graphRfProps.connectionRadius = graph.connectionRadius;
    if (graph.connectionMode !== undefined)
      graphRfProps.connectionMode = graph.connectionMode;
    graphRfProps.onInit = handleInit;
    if (graphInteractive) {
      // Mutation callbacks + drop + keyboard-delete are live only when editable.
      if (graph.onNodesChange) graphRfProps.onNodesChange = graph.onNodesChange;
      if (graph.onEdgesChange) graphRfProps.onEdgesChange = graph.onEdgesChange;
      if (graph.onConnect) graphRfProps.onConnect = graph.onConnect;
      if (graph.onConnectStart)
        graphRfProps.onConnectStart = graph.onConnectStart;
      if (graph.onConnectEnd) graphRfProps.onConnectEnd = graph.onConnectEnd;
      if (graph.onBeforeDelete)
        graphRfProps.onBeforeDelete = graph.onBeforeDelete;
      if (graph.onDelete) graphRfProps.onDelete = graph.onDelete;
      if (graph.onDragOver) graphRfProps.onDragOver = graph.onDragOver;
      if (graph.onDrop) graphRfProps.onDrop = handleDrop;
      graphRfProps.deleteKeyCode =
        graph.deleteKeyCode !== undefined
          ? graph.deleteKeyCode
          : DEFAULT_DELETE_KEYS;
    } else {
      // Read-only (Viewer): keyboard delete off; mutation callbacks omitted.
      graphRfProps.deleteKeyCode = null;
      if (allowReadOnlySelection) {
        if (graph.onNodesChange)
          graphRfProps.onNodesChange = (changes) => {
            const selections = changes.filter(
              (change) => change.type === "select",
            );
            if (selections.length) graph.onNodesChange?.(selections);
          };
        if (graph.onEdgesChange)
          graphRfProps.onEdgesChange = (changes) => {
            const selections = changes.filter(
              (change) => change.type === "select",
            );
            if (selections.length) graph.onEdgesChange?.(selections);
          };
      }
    }
  }

  // d3-zoom keeps the VIEWPORT RECT inside this, not just the content. So the margin has
  // to be wide enough to hold the visible world at `minZoom`, or zooming out pins the view
  // to a corner and the board slides to the top-left. See `panMargin`.
  const translateExtent: [[number, number], [number, number]] = [
    [-panMargin, -panMargin],
    [dimensions.w + panMargin, dimensions.h + panMargin],
  ];

  return (
    <div
      ref={setCanvasHosts}
      /* Stable semantic handle for the onboarding tours' canvas step. It sits
         on the FRAME rather than on a widget, because the step is about the
         canvas as a place to work — and a frame is always present and always
         has a real box, which a `display: contents` wrapper or an empty canvas
         would not. Generic on purpose: SOLVE, VSM and Hoshin all reach it
         through their own definitions, and this package learns no module. */
      data-tour-id="canvas.frame"
      data-canvas-tool={navigation.tool}
      className={cn(
        // `overflow-clip`, not `overflow-hidden`: a hidden box is still a scroll
        // container, so the browser's scroll-focused-element-into-view can shift
        // it sideways (e.g. focusing a floating control mid rail-transition) and
        // nothing ever scrolls it back — the HUD toolbars then strand mid-canvas.
        // `clip` clips identically but the box can never be scrolled at all.
        "relative flex-1 min-h-0 overflow-clip rounded-none border-0 bg-pb-page",
        frameClassName,
      )}
    >
      <ExpandedCardHostProvider host={expandedCardHost}>
        <CanvasNavigationContext.Provider value={navigation}>
          <ReactFlowProvider>
            {/* DS keep (PR 3): the React Flow canvas, its dot grid and mini-map
              are canvas rendering, not DS parts. */}
            <ReactFlow
              nodes={graph?.nodes ?? EMPTY_NODES}
              edges={graph?.edges ?? EMPTY_EDGES}
              nodesDraggable={graphInteractive && !panning}
              nodesConnectable={graphInteractive && !panning}
              elementsSelectable={
                graph != null &&
                (graphInteractive || allowReadOnlySelection) &&
                !panning
              }
              // Graph passthrough spreads BEFORE the Canvas-owned chrome props
              // below so Canvas always wins a future collision — it keeps sole
              // ownership of pan/zoom, viewport bounds, and the dot grid. Today
              // the key sets are disjoint; the parity tests pin the outcome.
              {...graphRfProps}
              // Pan/zoom UX: hold left mouse to pan (any blank canvas area).
              // Modules with long lists can switch the wheel from zoom to pan;
              // the default canvas gesture remains wheel zoom. `selectionOnDrag`
              // stays false so left-drag never starts a marquee selection.
              panOnDrag={true}
              // Scope Space to this canvas and leave native control keys alone.
              panActivationKeyCode={null}
              panOnScroll={wheelBehavior === "pan"}
              selectionOnDrag={false}
              zoomOnScroll={wheelBehavior === "zoom"}
              zoomOnPinch={true}
              zoomOnDoubleClick={false}
              // A click on empty canvas (never a pan — that's a drag — and never
              // a widget — those are a pointer-events overlay) deselects, clearing
              // the local ring and the awareness selection/activity together, then
              // runs the module's graph pane-click if any (Canvas deselects first).
              onPaneClick={handlePaneClick}
              minZoom={minZoom}
              maxZoom={maxZoom}
              translateExtent={translateExtent}
              fitView={false}
              defaultViewport={defaultViewport}
              proOptions={{ hideAttribution: true }}
              className={cn(
                "w-full h-full",
                readOnly && "pointer-events-auto",
                panning && CANVAS_PAN_CLASSES,
              )}
            >
              {/* Faint dot grid — matches the reference workbench (gap 24 / size
                1.4, drawn in --pb-border-strong over the page surface). The
                phase-tinted swimlanes from <PhaseSwimlanes/> in the `background`
                slot below sit on top of it. */}
              <Background
                variant={BackgroundVariant.Dots}
                gap={24}
                size={1.4}
                color="hsl(var(--pb-border-strong))"
              />
              {/* Optional built-in mini-map (bottom-right). Host toggles it via
                `showMiniMap`; Canvas owns the render as viewport chrome
                (ADR-0021). pb-token styled to match the canvas surface. */}
              {/* DS keep (PR 3): React Flow's own mini-map, pb-token painted. */}
              {showMiniMap ? (
                <MiniMap
                  pannable
                  zoomable
                  className="!bg-pb-card !border !border-pb-border"
                  maskColor="hsl(var(--pb-page) / 0.6)"
                  nodeColor="hsl(var(--pb-accent) / 0.6)"
                />
              ) : null}
            </ReactFlow>

            {/* Let the underlying Flow pane receive drags over every content
              layer in Hand mode. HUD controls and portalled editors stay live. */}
            <div
              ref={setContentHost}
              className="contents [&_:focus-visible]:outline [&_:focus-visible]:outline-2 [&_:focus-visible]:outline-pb-accent [&_:focus-visible]:outline-offset-2"
            >
              {/* Background overlay (module-specific layout: swimlanes, value-stream, etc.).
              DS keep (PR 3): a canvas layer positioned in viewport space. */}
              {background ? (
                <div
                  data-testid="canvas-background-slot"
                  style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                  }}
                >
                  <ScaledOverlay dimensions={dimensions}>
                    {background}
                  </ScaledOverlay>
                </div>
              ) : null}

              {/* Widgets overlay — outside the ReactFlow node graph by design.
              `showWidgets={false}` hides the whole layer for a framing that must
              not show widgets (e.g. VSM's swimlane-map framing); default true
              keeps every existing consumer unchanged. */}
              {showWidgets ? (
                <WidgetLayer
                  doc={doc}
                  widgetsMap={widgetsMap}
                  highlighted={highlightedWidgetId}
                  canvasW={dimensions.w}
                  canvasH={dimensions.h}
                  canvasMinX={canvasMinX}
                  canvasMinY={canvasMinY}
                  reservedTop={reservedTop}
                  readOnly={readOnly}
                  resolveWidgetReadOnly={resolveWidgetReadOnly}
                  resolveWidgetVisibility={resolveWidgetVisibility}
                  resolveWidgetLockState={resolveWidgetLockState}
                  awareness={awareness}
                  selected={selectedWidgetId}
                  onWidgetSelect={selectWidget}
                  onWidgetActivity={(activeWidget) =>
                    setCanvasAwarenessField(
                      awareness,
                      "activeWidget",
                      activeWidget,
                    )
                  }
                  solveId={solveId}
                  hostMetadata={widgetHostMetadata}
                  onWidgetRemove={onWidgetRemove}
                  onWidgetAskAI={onWidgetAskAI}
                  onWidgetDragEnd={onWidgetDragEnd}
                  onCanvasMutation={onCanvasMutation}
                />
              ) : null}

              <CanvasRevealController
                request={revealRequest}
                widgetsMap={widgetsMap}
                onHighlight={setHighlightedWidgetId}
              />

              {viewportOverlaySlot}
            </div>

            {/* Pinch over content React Flow cannot zoom from (PB-5441 BUG-14). */}
            <CanvasFocusVisibility
              root={canvasRoot}
              contentHost={contentHostEl}
            />
            <CanvasPinchForwarder
              root={canvasRoot}
              contentHost={contentHostEl}
              minZoom={minZoom}
              maxZoom={maxZoom}
            />

            {/* Ephemeral cursor presence, independently privacy-gated. A viewer
              may hide other people's cursors without unpublishing their own. */}
            {showRemoteCursors ? (
              <PresenceCursors awareness={cursorAwareness} />
            ) : null}
            <CanvasCursorPublisher
              root={canvasRoot}
              awareness={cursorAwareness}
            />

            {/* Module-specific top-center slot (e.g. SOLVE phase-strip pill).
              Sits inside ReactFlowProvider so children can call useReactFlow(). */}
            {topSlot ? (
              <div
                data-testid="canvas-top-slot"
                className="absolute top-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 z-10 pointer-events-none"
              >
                {topSlot}
              </div>
            ) : null}

            {/* Module-specific status overlay (e.g. SOLVE lock badge), top-start
              corner. Interactive (pointer-events-auto) but sized to its content
              so it never blocks canvas pan/zoom elsewhere. The box that sits at
              top-start carries the stamp, so a top-end WorkspaceControlGroup
              folds against it and the top-chrome inset counts it, whatever
              the slot holds (an inline group, a badge). */}
            {statusSlot ? (
              <div
                data-testid="canvas-status-slot"
                data-placement="top-start"
                className="absolute top-4 start-4 z-10 pointer-events-auto"
              >
                {statusSlot}
              </div>
            ) : null}

            {/* Collaboration (canvas) socket status — top-center, below any
              top slot. Hidden while connected. The container is click-through
              so it never blocks pan/zoom; only the banner pill is interactive
              (so its Retry action works). */}
            {connectionState && connectionState !== "connected" ? (
              <div
                data-testid="canvas-connection-slot"
                className="absolute start-1/2 z-20 -translate-x-1/2 pointer-events-none rtl:translate-x-1/2"
                style={{
                  top: "var(--pb-workspace-top-chrome-bottom, 4rem)",
                }}
              >
                <div className="pointer-events-auto">
                  <CanvasConnectionBanner
                    state={connectionState}
                    onRetry={onReconnect}
                  />
                </div>
              </div>
            ) : null}

            {hudSlot}

            {/* Module-specific toolbar (bottom-center). */}
            {toolbarSlot ? (
              <div
                data-testid="canvas-toolbar-slot"
                className="absolute bottom-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 z-10"
              >
                {toolbarSlot}
              </div>
            ) : null}
          </ReactFlowProvider>
        </CanvasNavigationContext.Provider>
      </ExpandedCardHostProvider>
    </div>
  );
}

/**
 * Applies the same viewport transform the WidgetLayer uses so a background
 * overlay (e.g. SOLVE's phase swimlanes) pans/zooms in lockstep with widgets.
 */
function ScaledOverlay({
  dimensions,
  children,
}: {
  dimensions: { w: number; h: number };
  children: React.ReactNode;
}): ReactElement {
  // Use the same viewport transform; reading via reactflow's useViewport inside
  // a sibling so the overlay receives the same x/y/zoom as the WidgetLayer.
  return (
    <BackgroundTransform>
      <div
        style={{
          width: dimensions.w,
          height: dimensions.h,
          position: "relative",
        }}
      >
        {children}
      </div>
    </BackgroundTransform>
  );
}

function BackgroundTransform({
  children,
}: {
  children: React.ReactNode;
}): ReactElement {
  const { x, y, zoom } = useViewport();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        transformOrigin: "0 0",
        transform: `translate(${x}px, ${y}px) scale(${zoom})`,
        pointerEvents: "none",
      }}
    >
      {children}
    </div>
  );
}
